import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import CallService from '../services/callService'
import { CustomerRoles } from '../constants/customerRoles'
import { PermissionStates } from '../constants/permissionStates'

//* ASYNC THUNK ACTIONS
export const unsubscribeNotifications = createAsyncThunk(
  'call/unsubscribeNotifications',
  async () => {
    try {
      await CallService.unsubscribeNotifications()
    } catch (err) {
      console.error(err)
    }
  },
)

export const toggleAudioCall = createAsyncThunk('call/toggleAudio', async (_, { getState }) => {
  const { isMute } = getState().call
  await CallService.setAudioTrackStatus(isMute)
  return !isMute
})

export const setTermsAndConditions = createAsyncThunk(
  'call/setTermsAndConditions',
  ({ countryCode, customTermsAndConditions, autoAcceptTermsAndConditions }, { dispatch }) => {
    const termsAndConditionsAccepted = customTermsAndConditions !== true

    if (termsAndConditionsAccepted || autoAcceptTermsAndConditions === true) {
      dispatch(setTermsAndConditionsAccepted(true))
    }
    return countryCode
  },
)

export const callSlice = createSlice({
  name: 'call',
  initialState: {
    options: {
      channel: '',
    },
    country: 'AR',
    permissionStatus: PermissionStates.PENDING,
    role: CustomerRoles.BUYER,
    leaveCallError: '',
    termsAndConditionsAccepted: false,
    displayFinishEventCountdown: false,
    displayStartEventCountdown: false,
    displayPopUp: false,
    sellerIsOnCall: true,
    sellerNetworkError: false,
    buyerPoorNetwork: false,
    audioPermission: false,
    videoPermission: false,
    audioOrVideoPermission: false,
    seller: {
      rtcUID: 2,
      rtmUID: null,
    },
    cohost: {
      rtcUID: 3,
      rtmUID: null,
    },
    externalVideo: {
      rtcUID: 4,
      rtmUID: null,
    },
    localTracks: {
      camera: {
        track: null,
        enabled: false,
        facingMode: null,
      },
      microphone: {
        track: null,
        enabled: false,
        mutedBySeller: false,
      },
    },
    users: [],
    usersCount: undefined,
    lastVideo: false,
  },
  reducers: {
    setTermsAndConditionsAccepted: (state, action) => {
      state.termsAndConditionsAccepted = action.payload
    },
    setSellerNetworkError: (state, action) => {
      state.sellerNetworkError = action.payload
      state.sellerIsOnCall = !action.payload
    },
    setAudioPermission: (state, action) => {
      state.audioPermission = action.payload
    },
    setVideoPermission: (state, action) => {
      state.videoPermission = action.payload
    },
    setAudioOrVideoPermission: (state, action) => {
      state.audioOrVideoPermission = action.payload
    },
    setBuyerPoorNetwork: (state, action) => {
      state.buyerPoorNetwork = action.payload
    },
    setLocalCameraTrack: (state, action) => {
      state.localTracks.camera.track = action.payload
    },
    enableLocalCameraTrack: (state, action) => {
      state.localTracks.camera.enabled = action.payload
    },
    setLocalMicrophoneTrack: (state, action) => {
      state.localTracks.microphone.track = action.payload
    },
    enableLocalMicrophoneTrack: (state, action) => {
      state.localTracks.microphone.enabled = action.payload
    },
    changePermissionStatus: (state, action) => {
      state.permissionStatus = action.payload
    },
    setDisplayStartEventCountdown: (state, action) => {
      state.displayStartEventCountdown = action.payload
    },
    setDisplayFinishEventCountdown: (state, action) => {
      state.displayFinishEventCountdown = action.payload
    },
    setDisplayPopUpState: (state, action) => {
      state.displayPopUp = action.payload
    },
    removeUser: (state, action) => {
      const { userUid } = action.payload
      const newUsers = state.users.filter((user) => user.id !== userUid)
      state.users = newUsers
    },
    addUser: (state, action) => {
      const newUser = action.payload.user.text

      state.users.push(newUser)
    },
    addUsers: (state, action) => {
      const newUsers = action.payload.users

      state.users = newUsers
    },
    setUsersCount: (state, action) => {
      state.usersCount = action.payload
    },
    setMutedByPeer: (state, action) => {
      state.localTracks.microphone.mutedBySeller = action.payload
    },
    setLocalCameraFacingMode: (state, action) => {
      state.localTracks.camera.facingMode = action.payload
    },
    setVideoState: (state, action) => {
      const { rtcUID, videoTrack } = action.payload
      const [user] = state.users.filter((user) => user.rtcUID === rtcUID)
      if (videoTrack) {
        user.videoState = true
        state.lastVideo = rtcUID
      } else {
        user.videoState = false
        state.lastVideo = null
      }
    },
  },
  extraReducers: {
    [setTermsAndConditions.fulfilled]: (state, action) => {
      state.country = action.payload
    },
  },
})

//* ACTIONS
export const {
  setTermsAndConditionsAccepted,
  setSellerNetworkError,
  setBuyerPoorNetwork,
  setLocalCameraTrack,
  enableLocalCameraTrack,
  setLocalMicrophoneTrack,
  enableLocalMicrophoneTrack,
  removeUser,
  addUser,
  addUsers,
  setUsersCount,
  setMutedByPeer,
  setLocalCameraFacingMode,
  setAudioPermission,
  setVideoPermission,
  setAudioOrVideoPermission,
  setVideoState,
  setDisplayFinishEventCountdown,
  setDisplayStartEventCountdown,
  setDisplayPopUpState,
  addNewMessageCount,
} = callSlice.actions

//* SELECTORS
export const selectTermsAndConditionsAccepted = (state) => state.call.termsAndConditionsAccepted
export const selectCountry = (state) => state.call.country
export const selectRole = (state) => state.call.role
export const selectPermissionState = (state) => state.call.permission
export const selectSellerNetworkError = (state) => state.call.sellerNetworkError
export const selectSellerIsOnCall = (state) => state.call.sellerIsOnCall
export const selectBuyerPoorNetwork = (state) => state.call.buyerPoorNetwork
export const selectAudioPermission = (state) => state.call.audioPermission
export const selectVideoPermission = (state) => state.call.videoPermission
export const selectAudioOrVideoPermission = (state) => state.call.audioOrVideoPermission
export const selectLocalCameraTrack = (state) => state.call.localTracks.camera.track
export const selectLocalCameraFacingMode = (state) => state.call.localTracks.camera.facingMode
export const selectLocalCameraEnabled = (state) => state.call.localTracks.camera.enabled
export const selectLocalMicrophoneTrack = (state) => state.call.localTracks.microphone.track
export const selectLocalMicrophoneEnabled = (state) => state.call.localTracks.microphone.enabled
export const selectUsers = (state) => state.call.users
export const selectUsersCount = (state) => state.call.usersCount
export const selectMutedByPeer = (state) => state.call.localTracks.microphone.mutedBySeller
export const selectLastVideo = (state) => state.call.lastVideo
export const selectDisplayFinishEventCountdownstate = (state) =>
  state.call.displayFinishEventCountdown
export const selectDisplayStartEventCountdownstate = (state) =>
  state.call.displayStartEventCountdown
export const selectDisplayPopUpState = (state) => state.call.displayPopUp
export const selectCohostRTCuid = (state) => state.call.cohost.rtcUID

//* REDUCER
export default callSlice.reducer
